/* eslint-disable arrow-body-style */
import {action, autorun, computed, makeObservable, observable, reaction} from "mobx";

import {AppConfiguration} from "@/core/config/app-configuration";
import {ApiConfiguration} from "@/config/api-configuration";

import {MapState} from "@/core/map-state";

import {MapViewerConfiguration} from "@/config/map-viewer-configuration";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";

import {LayerObjectType} from "./layer-object-type";

import {IStore} from "./abstract-store";
import {GenericLayerEffect} from "@/layer-effects/generic-layer-effect";
import {LayerEffect} from "@/layer-effects/layer-effect";

import {BuildingsStore} from "./buildings-store-mobx";
import {Building} from "@/models/building";
import {PerimeterStore} from "./perimeter-store-mobx";
import {TopicConfiguration} from "@/config/topic-configuration";
import TimeExtent from "@arcgis/core/TimeExtent";
import {AppState} from "@/components/app/app-mobx";
import {UiState} from "@/core/ui-state";
import {AbstractAppStore} from "@/core/stores/abstract-app-store";
import {ProcessSourcesStore} from "./process-sources-store-mobx";
import {MunicipalityStore} from "./municipality-store-mobx";
import {SteckbriefStore} from "./steckbrief-store-mobx";
import {Municipality} from "@/models/municipality";
import {ProcessSource} from "@/models/process-source";
import {Steckbrief} from "@/models/steckbrief";

export interface RisikoanalyseProps {
    appConfig: AppConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    appStore: AppState;
    mapStore: MapState;
    uiStore: UiState;
}

export type RisikoanalyseStatus = "loading" | "successful" | "error" | "not-found" | null;

export type EditingObjectType = "perimeter";

export type DataStoreType = MunicipalityStore | BuildingsStore | PerimeterStore | ProcessSourcesStore | SteckbriefStore;


export class RisikoanalyseStore extends AbstractAppStore {
    loading: boolean;
    status: RisikoanalyseStatus;

    isEditing = false;
    editingObjectType: EditingObjectType | null = null;
    isSelected = false;

    perimeterGeometry: __esri.Geometry | null = null;
    timeExtent: TimeExtent;
    data: any;

    /**
     * Domains stored by domain name
     */
    domains = observable.map<string, __esri.CodedValueDomain>();
    /**
     * Domains stored by field name
     */
    domainsByField =  observable.map<string, __esri.CodedValueDomain>();

    /**
     * Applied filters per group
     */
    filters = observable.map<string, string[]>();

    apiLoaded = false;
    apiLoading = false;
    apiConfig: ApiConfiguration;

    geoDataStores: Map<string, DataStoreType> = new Map<string, DataStoreType>();

    layerEffects: Map<string, LayerEffect> = new Map<string, LayerEffect>();

    constructor(props: RisikoanalyseProps) {

        super(props);
        makeObservable(this, {
            loading: observable,
            status: observable,
            isEditing: observable,
            editingObjectType: observable,
            isSelected: observable,
            perimeterGeometry: observable,
            timeExtent: observable,
            data: observable,
            domains: observable,
            domainsByField: observable,
            filters: observable,
            apiLoaded: observable,
            apiLoading: observable,

            layerEffects: observable,

            selectedBuilding: computed,
            isEditingProjectPerimeter:computed,
            isPerimeterSelected: computed,

            resetSelect: action,
            setStatus: action,
            resetStatus: action,
            setFilter: action,
            resetFilters: action,
            setApiLoading: action,
            setPerimeterGeometry: action,
            clearPerimeterGeometry: action,
            setData: action,
            clearData: action,
            setTimeExtent: action,
            setEditing: action,
            setPerimeterSelected: action,
            loadApi: action,
        });
        this.apiConfig = props.mapViewerConfig.api;

        // init the stores
        this.initGeoDataStores(this.apiConfig);
        this.initLayerEffects();
    }

    get PerimeterStore(): PerimeterStore {
        return this.geoDataStores.get("project-perimeter") as PerimeterStore;
    }

    get BuildingsStore(): BuildingsStore {
        return this.geoDataStores.get("building") as BuildingsStore;
    }
    get ProcessSourcesStore(): ProcessSourcesStore {
        return this.geoDataStores.get("process-source") as ProcessSourcesStore;
    }
    get MunicipalityStore(): MunicipalityStore {
        return this.geoDataStores.get("municipality") as MunicipalityStore;
    }
    get SteckbriefStore(): SteckbriefStore {
        return this.geoDataStores.get("steckbrief") as SteckbriefStore;
    }

    get selectedBuilding(): Building | null {
        return this.BuildingsStore?.selectedItem as Building;
    }

    get isEditingProjectPerimeter(): boolean {
        return this.isEditing && this.editingObjectType === "perimeter";
    }
    get isPerimeterSelected(): boolean {
        return this.isSelected;
    }

    resetSelect(): void {
        this.BuildingsStore?.resetSelectedItem();
    }

    setStatus(status: RisikoanalyseStatus): void {
        this.status = status;
    }
    resetStatus(): void {
        this.status = null;
    }

    setFilter(group: string, filter: string[]): void {
        this.filters.set(group, filter);
    }
    resetFilters(): void {
        this.filters.clear();
    }

    setApiLoading(flag: boolean, loadedFlag?: boolean): void {
        this.apiLoading = flag;
        if (typeof loadedFlag !== "undefined") {
            this.apiLoaded = loadedFlag;
        }
    }

    setPerimeterGeometry(geometry: __esri.Geometry | null): void {
        this.perimeterGeometry = geometry;
    }
    clearPerimeterGeometry(): void {
        this.perimeterGeometry = null;
    }

    setData(data: any | null): void {
        this.data = data;
    }
    clearData(): void {
        this.data = null;
    }

    setTimeExtent(timeExtent: TimeExtent): void {
        this.timeExtent = timeExtent;
    }

    setEditing(editing: boolean, objectType?: EditingObjectType): void {
        this.isEditing = editing;
        this.editingObjectType = objectType || null;
    }

    setPerimeterSelected(selected: boolean) {
        this.isSelected = selected;
    }

    /**
     * Get domain and mep to dropdown item options
     *
     * @param layer
     * @param fieldName
     * @private
     */
    getDomainAsOptions(layer: LayerObjectType, fieldName: string): Promise<DropdownItemProps[] | null> {
        return new Promise((resolve) => {
            this.loadDomain(layer, fieldName).then((domain: __esri.CodedValueDomain) => {
                const options: DropdownItemProps[] = [];

                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    options.push({
                        key: codedValue.code,
                        text: codedValue.name,
                        value: codedValue.code
                    });
                });
                resolve(options);
            });
        });
    }
    getShortDomainCodes(layerName: LayerObjectType, fieldName: string): Promise<Map<number, string> | null> {
        return new Promise((resolve) => {
            this.loadDomain(layerName, fieldName).then((domain: __esri.CodedValueDomain) => {
                const codes = new Map<number, string>();
                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    const key = typeof codedValue.code === "string" ? parseInt(codedValue.code, 10) : codedValue.code;
                    codes.set(key, codedValue.name);
                });
                resolve(codes);
            });
        });
    }

    getStringDomainCodes(layerName: LayerObjectType, fieldName: string): Promise<Map<string, string> | null> {
        return new Promise((resolve) => {
            this.loadDomain(layerName, fieldName).then((domain: __esri.CodedValueDomain) => {
                const codes = new Map<string, string>();
                domain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                    const key = typeof codedValue.code === "number" ? codedValue.code.toString() : codedValue.code;
                    codes.set(key, codedValue.name);
                });
                resolve(codes);
            });
        });
    }

    /**
     * Load coded value domain by field name
     *
     * @param type Type of date. Is used to select the layer.
     * @param fieldName The name of the field the domain is related to
     */
    loadDomain(type: LayerObjectType, fieldName: string): Promise<__esri.CodedValueDomain | null> {
        return new Promise((resolve) => {
            autorun((reaction: any) => {
                if (!this.apiLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun

                if (!fieldName) {
                    throw new Error("Field name undefined");
                }
                const dbField = this.getDatabaseFieldName(type, fieldName);
                if (!dbField) {
                    throw new Error(`Database field not found for ${type} ${fieldName}`);
                }

                const store = this.geoDataStores.get(type);
                if (!store) {
                    throw new Error(`Store not found for ${type} ${fieldName}`);
                }

                store.loadDomain(dbField).then((domain: any) => {
                    resolve(domain);
                });
            });
        });
    }

    loadApi() {
        if (!this.apiLoaded && !this.apiLoading) {
            this.setApiLoading(true);

            // load all stores. Loads the feature layers for each store
            const stores = [...this.geoDataStores.values()];
            const apiPromises = stores.map((store: IStore) => {
                return store.loadApi();
            });
            Promise.all(apiPromises).then(() =>{
                // all feature layers loaded
                this.setApiLoading(false, true);
            });
        }
    }

    getStoreByTopic(topic: TopicConfiguration | undefined): BuildingsStore {
        const topicId = topic?.id || "building";
        switch(topicId) {
        case "building": {
            return this.BuildingsStore;
        }
        default: {
            return this.BuildingsStore;
        }
        }
    }

    /**
     * Get field alias name for an object property
     * @param type
     * @param propertyName
     */
    getFieldAlias(type: LayerObjectType, propertyName: string): string | undefined {
        const dbFieldName = this.getDatabaseFieldName(type, propertyName);
        if (!dbFieldName) {
            // eslint-disable-next-line no-console
            console.warn(`No database field found for ${type} ${propertyName}`);
            return propertyName;
        }

        if (!this.apiLoaded) {
            // API should be loaded when domains are requested the first time
            throw new Error("API not loaded.");
        }

        const field = this.geoDataStores.get(type)?.getField(dbFieldName);
        return field?.alias;
    }

    /**
     * Initialize all layer effects
     * @private
     */
    private initLayerEffects(): void {
        const genericEffect = new GenericLayerEffect({store: this});
        this.layerEffects.set(
            "generic",
            genericEffect
        );

        /**
         * Apply effect
         */
        reaction(
            () => [genericEffect.ready, this.appStore.subtopicId],
            () => {
                genericEffect.apply();
            }
        );
        /*
        this.mapStore.view?.when(()=> {
            genericEffect.apply();
        });
        */
        /*
        reactiveUtils.whenOnce(
            () => this?.mapStore.view?.ready === true)
            .then(() => {
                genericEffect.apply();
            });
         */
    }

    /**
     * Returns the name of this field in database.
     * Uses field name mappings in model classes.
     *
     * @param type
     * @param fieldName
     * @private
     */
    private getDatabaseFieldName(type: LayerObjectType, fieldName: string): string | undefined {
        switch(type) {
        case "building": {
            return Building.databaseFieldNames.get(fieldName);
        }
        case "municipality": {
            return Municipality.databaseFieldNames.get(fieldName);
        }
        case "process-source": {
            return ProcessSource.databaseFieldNames.get(fieldName);
        }
        case "steckbrief": {
            return Steckbrief.databaseFieldNames.get(fieldName);
        }
        default: {
            throw new Error(`Could not get database field name. Type unknown. ${type}`);
        }
        }
    }

    /**
     * Init all data stores. Do this before loadApi()!!!
     * @param apiConfig
     * @private
     */
    private initGeoDataStores(apiConfig: ApiConfiguration): void {
        this.initProcessSourceStore(apiConfig);
        this.initMunicipalityStore(apiConfig);
        this.initSteckbriefStore(apiConfig);
    }

    private initProcessSourceStore(apiConfig: ApiConfiguration): void {
        const storeType = "process-source";
        const layerConfig = apiConfig.getLayerConfig(storeType);
        if (!layerConfig) {
            throw new Error(`No layer configuration found for ${storeType}!`);
        }
        const store = new ProcessSourcesStore({
            layerConfig,
            mapStore: this.mapStore
        });
        this.geoDataStores.set(
            storeType,
            store
        );
    }
    private initMunicipalityStore(apiConfig: ApiConfiguration): void {
        const storeType = "municipality";
        const layerConfig = apiConfig.getLayerConfig(storeType);
        if (!layerConfig) {
            throw new Error(`No layer configuration found for ${storeType}!`);
        }
        const store = new MunicipalityStore({
            layerConfig,
            mapStore: this.mapStore
        });
        this.geoDataStores.set(
            storeType,
            store
        );
    }
    private initSteckbriefStore(apiConfig: ApiConfiguration): void {
        const storeType = "steckbrief";
        const layerConfig = apiConfig.getLayerConfig(storeType);
        if (!layerConfig) {
            throw new Error(`No layer configuration found for ${storeType}!`);
        }
        const store = new SteckbriefStore({
            layerConfig
        });
        this.geoDataStores.set(
            storeType,
            store
        );
    }
}


